import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import type { IDefaultInput } from "libs/design-system/src/types/Input/input";
import type { IPhoneInputProps } from "libs/design-system/src/types/phone_input";
import { iso2ToIso3, iso3ToIso2 } from "@unified-checkout/design-system";

import { COUNTRIES_INFO } from "@app/dashboard/constants/countries";
import { useCheckoutCustomizationStore } from "@app/dashboard/store/checkout-customization";
import {
  BUSINESS_INFO_MAX_CHARS,
  CUSTOMIZATION_SECTIONS_KEYS
} from "../../../../utils";
import { phoneNumberValidation } from "@app/dashboard/utils/formValidations";
import { InputsGroup } from "@app/dashboard/components/inner_layout/InputsGroup";


const FIELDS_NAMES = {
  ADDRESS: "checkout_address",
  CITY: "checkout_city",
  PHONE_NUMBER: "checkout_phone_number"
};

const { BUSINESS_INFO } = CUSTOMIZATION_SECTIONS_KEYS;

type TInputProps = (IDefaultInput | IPhoneInputProps)[];

export const BusinessInfo = () => {
  const { t } = useTranslation();

  const {
    isFetched,
    isDataReset,
    customizationData,
    setCustomizationData,
    errors,
    setErrors,
  } = useCheckoutCustomizationStore();

  const { branding } = customizationData;
  const {
    checkout_address,
    checkout_city,
    checkout_selected_countery,
    checkout_phone_number
  } = branding;

  const iso2CountryCode = iso3ToIso2(checkout_selected_countery || '');
  const isEg = iso2CountryCode === 'eg';

  const validatePhoneNumber = (phone: string, iso2CountryCode: string) => {
    if (!phone) return true;

    const iso3CountryCode = iso2ToIso3(iso2CountryCode);
    const validationRule = phoneNumberValidation(iso3CountryCode);
    const phoneCountryCode = COUNTRIES_INFO[iso3CountryCode].phone_country_code;

    const phoneWithoutCountryCode = phone.replace(phoneCountryCode, '');

    return validationRule.pattern.test(phoneWithoutCountryCode);
  };

  const onChangeHandler = (fieldName: string, value: string, iso2CountryCode?: string) => {
    setErrors(fieldName, "");

    const isPhoneNumberField = fieldName === FIELDS_NAMES.PHONE_NUMBER;
    const isEg = iso2CountryCode === 'eg';
    const countryCode = iso2CountryCode || iso3ToIso2(checkout_selected_countery || '');
    const iso3CountryCode = iso2ToIso3(iso2CountryCode || '');
    const relatedPhoneCountryCode = COUNTRIES_INFO[iso3CountryCode].phone_country_code;

    if (isPhoneNumberField) {
      const isValidPhone = validatePhoneNumber(value, iso2CountryCode || '');
      setErrors(fieldName, isValidPhone ? '' : t('errors.invalidPhone'));
    };

    setCustomizationData(prevData => ({
      ...prevData,
      branding: {
        ...prevData.branding,
        ...(isPhoneNumberField ? {
          checkout_phone_number: isEg ? value.replace('0', '') : value,
          checkout_selected_countery: iso3CountryCode,
          selectedCountryCode: isEg ? `${relatedPhoneCountryCode}0` : relatedPhoneCountryCode
        } : {
          [fieldName]: value
        })
      }
    }));

    setInputsProps(prevState =>
      prevState?.map(input => {
        if (isPhoneNumberField && input.name === FIELDS_NAMES.PHONE_NUMBER) {
          return {
            ...input,
            phoneNumber: value,
            country: countryCode,
            error: validatePhoneNumber(value, iso2CountryCode || '') ? '' : ' '
          }
        } else if (input.name === fieldName) {
          return {
            ...input,
            value: value,
          }
        }
        return input;
      })
    );
  };

  const handlePhoneChange = (fieldName: string, phone: string, country?: string) => {
    const iso3CountryCode = iso2ToIso3(country || '');
    const phoneCountryCode = COUNTRIES_INFO[iso3CountryCode].phone_country_code;
    const numWithoutPhoneCountryCode = phone.replace(phoneCountryCode, '');

    onChangeHandler(fieldName, numWithoutPhoneCountryCode, country);
  };

  const defaultInputProps: TInputProps = [
    {
      name: FIELDS_NAMES.ADDRESS,
      placeholder: t('innerLayout.checkoutCustomizationPage.tabs.branding.sections.businessInfo.placeholders.address'),
      value: '',
      onChange: (e) => onChangeHandler(FIELDS_NAMES.ADDRESS, e.target.value)
    },
    {
      name: FIELDS_NAMES.CITY,
      placeholder: t('innerLayout.checkoutCustomizationPage.tabs.branding.sections.businessInfo.placeholders.city'),
      value: '',
      onChange: (e) => onChangeHandler(FIELDS_NAMES.CITY, e.target.value)
    },
    {
      name: FIELDS_NAMES.PHONE_NUMBER,
      phoneNumber: '',
      country: iso2CountryCode,
      onChangeHandler: (phone: string, country?: string) => handlePhoneChange(FIELDS_NAMES.PHONE_NUMBER, phone, country),
      error: '',
    }
  ];

  const [inputsProps, setInputsProps] = useState<TInputProps>();

  useEffect(() => {
    if (isFetched) {

      const phoneNum = checkout_phone_number ?
        ((isEg && !checkout_phone_number.length) ? `0${checkout_phone_number}` : checkout_phone_number)
        :
        '';

      setInputsProps(() => (
        defaultInputProps.map(input => {
          return {
            ...input,
            ...(input.name === FIELDS_NAMES.PHONE_NUMBER ?
              {
                country: iso2CountryCode,
                phoneNumber: phoneNum,
              }
              : []),
            value:
              input.name === FIELDS_NAMES.ADDRESS ?
                checkout_address || ''
                :
                input.name === FIELDS_NAMES.CITY ?
                  checkout_city || ''
                  : ''
          }
        }
        )));

      handlePhoneChange(FIELDS_NAMES.PHONE_NUMBER, phoneNum, iso2CountryCode);
    };

  }, [isFetched, isDataReset]);

  return (
    inputsProps && <InputsGroup
      label={t('innerLayout.checkoutCustomizationPage.tabs.branding.sections.businessInfo.title')}
      inputsProps={inputsProps}
      maxCharacters={BUSINESS_INFO_MAX_CHARS}
      onMaxCharsErrorHandler={(errMsg) => setErrors(BUSINESS_INFO, errMsg)}
      error={errors[FIELDS_NAMES.PHONE_NUMBER]}
    />
  )
}
